import React from "react";
import {
  Platform,
  type StyleProp,
  type ViewStyle,
  type TextStyle,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ThemedButton } from "@/components/ThemedButton";
import { CustomAlert } from "@/components/ThemedAlert";
import { useThemeColor } from "@/hooks/useThemeColor";
import * as ImagePicker from "expo-image-picker";

/**
 * Properties for the standalone UploadMedia component.
 */
export interface IUploadMediaProps {
  /** Callback triggered when media assets are selected from library */
  onMediaSelected: (assets: ImagePicker.ImagePickerAsset[] | any[]) => Promise<void> | void;
  /** Convenience callback for single asset selection */
  onAssetSelected?: (asset: ImagePicker.ImagePickerAsset | any) => Promise<void> | void;
  /** Allowed media types (defaults to ["images"]) */
  mediaTypes?: ("images" | "videos" | "livePhotos")[];
  /** Whether to allow multiple file selection (defaults to false) */
  allowsMultipleSelection?: boolean;
  /** Whether to allow image cropping/editing on native (defaults to false) */
  allowsEditing?: boolean;
  /** Aspect ratio for image cropping [width, height] */
  aspect?: [number, number];
  /** Compression quality between 0.0 and 1.0 (defaults to 0.9) */
  quality?: number;
  /** Whether the control is disabled */
  disabled?: boolean;
  /** Whether an upload or processing operation is active */
  uploading?: boolean;
  /** Button title label (defaults to "Upload Photo") */
  title?: string;
  /** Button style variant */
  variant?: "primary" | "secondary" | "minimal" | "danger";
  /** Optional container style override */
  style?: StyleProp<ViewStyle>;
  /** Optional text style override */
  textStyle?: StyleProp<TextStyle>;
  /** Custom trigger render function */
  renderTrigger?: (onPress: () => void, disabled: boolean) => React.ReactNode;
}

/**
 * Standalone Media Upload & Library Picker component.
 * Handles photo/video selection from the device media library or file system,
 * platform permissions, cropping configurations, and custom trigger rendering.
 *
 * @param {IUploadMediaProps} props - Component properties.
 * @returns {React.JSX.Element} The rendered UploadMedia component.
 */
export function UploadMedia({
  onMediaSelected,
  onAssetSelected,
  mediaTypes = ["images"],
  allowsMultipleSelection = false,
  allowsEditing = false,
  aspect,
  quality = 0.9,
  disabled = false,
  uploading = false,
  title = "Upload Photo",
  variant = "secondary",
  style,
  textStyle,
  renderTrigger,
}: IUploadMediaProps): React.JSX.Element {
  const tintColor = useThemeColor({}, "tint");
  const headerBgColor = useThemeColor({}, "buttonSecondary");
  const borderColor = useThemeColor({}, "border");

  const isDisabled = disabled || uploading;

  /**
   * Triggers the media library picker across web and native platforms.
   */
  const handleTrigger = async (): Promise<void> => {
    if (isDisabled) return;

    try {
      if (Platform.OS !== "web") {
        const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
        if (status !== ImagePicker.PermissionStatus.GRANTED && (status as any) !== "granted") {
          CustomAlert.alert(
            "Permission Required",
            "Please grant photo and media library access to select media."
          );
          return;
        }
      }

      const pickerResult = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: mediaTypes as any,
        allowsMultipleSelection,
        allowsEditing,
        aspect,
        quality,
        exif: true,
      });

      if (pickerResult.canceled || !pickerResult.assets || pickerResult.assets.length === 0) {
        return;
      }

      if (onAssetSelected && pickerResult.assets.length > 0) {
        await onAssetSelected(pickerResult.assets[0]);
      }

      await onMediaSelected(pickerResult.assets);
    } catch (err: any) {
      console.error("[UploadMedia] Error selecting media from library:", err);
      CustomAlert.alert("Selection Error", err?.message || "Failed to select media files.");
    }
  };

  if (renderTrigger) {
    return <>{renderTrigger(handleTrigger, isDisabled)}</>;
  }

  return (
    <ThemedButton
      title={title}
      icon={<Ionicons name="cloud-upload-outline" size={16} color={tintColor} />}
      variant={variant}
      disabled={isDisabled}
      onPress={handleTrigger}
      style={[
        { backgroundColor: headerBgColor, borderColor },
        style,
      ]}
      textStyle={textStyle}
      accessibilityLabel={title}
      accessibilityRole="button"
    />
  );
}

export default UploadMedia;
